'use client';

import { useCallback, useTransition } from 'react';
import { useBrowserState } from '@/src/lib/use-browser-state';
import { setPowerModeAction } from '@/src/server/actions/set-active-context.action';
import type { NavigationContext } from '@/src/lib/navigation';

export interface PowerModeWorkflowsBannerProps {
  activeContext: NavigationContext;
  canUsePowerMode: boolean;
}

const DISMISS_STORAGE_KEY = 'kontora_power_mode_banner_dismissed_v1';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function readDismissed(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function PowerModeWorkflowsBanner({
  activeContext,
  canUsePowerMode,
}: PowerModeWorkflowsBannerProps) {
  const [dismissed, setDismissed] = useBrowserState(readDismissed, false);
  const [isPending, startTransition] = useTransition();

  const handleDismiss = useCallback(() => {
    if (isBrowser()) {
      try {
        window.sessionStorage.setItem(DISMISS_STORAGE_KEY, '1');
      } catch {
        // SessionStorage nicht verfügbar – still ignorieren
      }
    }
    setDismissed(true);
  }, [setDismissed]);

  const handleDisable = useCallback(() => {
    startTransition(() => {
      const fd = new FormData();
      fd.set('enabled', 'false');
      fd.set('context', activeContext);
      void setPowerModeAction(fd);
    });
  }, [activeContext]);

  if (dismissed) return null;

  return (
    <div
      role="status"
      data-tour="power-mode-workflows-banner"
      className="mb-4 flex flex-wrap items-start gap-3 rounded-[var(--k-radius-lg)] border border-[var(--k-color-role-system-icon)]/30 bg-[var(--k-color-area-system-soft)] px-4 py-3"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--k-color-role-system-icon)] text-xs font-bold text-white"
      >
        ⚡
      </span>
      <div className="flex-1 text-sm">
        <p className="font-semibold text-[var(--k-color-role-system-text)]">
          Power-Mode aktiv — geführte Assistenten sind ausgeblendet.
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-[var(--k-color-text-secondary)]">
          Workflows und Tour-Assistenten werden im Power-Mode unterdrückt. Schalte ihn aus, um die
          geführten Abläufe wieder zu sehen.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {canUsePowerMode ? (
          <button
            type="button"
            onClick={handleDisable}
            disabled={isPending}
            className="shrink-0 rounded-[var(--k-radius-sm)] border border-[var(--k-color-role-system-icon)]/40 bg-[var(--k-color-surface)] px-2.5 py-1 text-[11px] font-semibold text-[var(--k-color-role-system-text)] transition hover:border-[var(--k-color-role-system-icon)] disabled:cursor-wait disabled:opacity-60"
          >
            {isPending ? '…' : 'Power-Mode deaktivieren'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 rounded-[var(--k-radius-sm)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-2.5 py-1 text-[11px] font-semibold text-[var(--k-color-text-secondary)] transition hover:border-[var(--k-color-border-strong)] hover:text-[var(--k-color-text-primary)]"
        >
          Heute nicht mehr anzeigen
        </button>
      </div>
    </div>
  );
}
